/** Reading position: the message at the viewport top plus pixels past it. */
export interface ScrollAnchor {
  messageId: string;
  delta: number;
}

export function chatScrollAnchorKey(scrollRestorationId: string): string {
  return `chat-anchor-${scrollRestorationId}`;
}

export function writeScrollAnchor(key: string, anchor: ScrollAnchor): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(anchor));
  } catch {
    // sessionStorage can throw in private mode; ignore.
  }
}

export function readScrollAnchor(key: string): ScrollAnchor | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ScrollAnchor> | null;
    return typeof parsed?.messageId === 'string' &&
      typeof parsed?.delta === 'number'
      ? { messageId: parsed.messageId, delta: parsed.delta }
      : null;
  } catch {
    return null;
  }
}

/**
 * A saved offset may include the height of a reasoning panel following the
 * anchor message. Those panels intentionally reopen collapsed, so replaying
 * the old offset against the shorter layout can jump past content. Restrict
 * the offset to the anchor row itself while preserving legitimate positions
 * inside long message rows and small negative offsets for date separators.
 * Returns null until the anchor row has a usable measurement.
 */
export function clampScrollAnchorDelta(
  delta: number,
  anchorItemSize: number
): number | null {
  if (!Number.isFinite(anchorItemSize) || anchorItemSize <= 0) return null;
  if (!Number.isFinite(delta)) return 0;
  const extent = anchorItemSize;
  return Math.max(-extent, Math.min(delta, extent));
}
